import { useMemo, useState } from 'react';
import { useAnimeFxContext } from '@/components/fx';
import './SakuraOverlay.css';

/**
 * Petals take the restaurant's own brand colours instead of the four
 * cherry-blossom pinks they used to be hard-coded to — falling sakura read as
 * a Japanese restaurant whatever the kitchen served. Softened toward white so
 * a dark brand colour still falls as a petal and not as a blot.
 */
const PETAL_TINT_VARS = ['--hot-pink', '--mint', '--purple', '--magenta'];

function petalColor(i: number): string {
  return `color-mix(in oklab, var(${PETAL_TINT_VARS[i % PETAL_TINT_VARS.length]}) 45%, white)`;
}

interface Petal {
  left: number;
  size: number;
  delay: number;
  duration: number;
  color: string;
  alt: boolean;
}

function safeMatch(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try { return window.matchMedia(query).matches; } catch { return false; }
}

function makePetals(count: number, durationMin: number, durationSpan: number): Petal[] {
  return Array.from({ length: count }, (_, i) => ({
    left: Math.random() * 100,
    size: 18 + Math.random() * 18,
    delay: -Math.random() * durationMin,
    duration: durationMin + Math.random() * durationSpan,
    color: petalColor(i),
    alt: i % 2 === 0,
  }));
}

export default function SakuraOverlay() {
  const { fxEnabled } = useAnimeFxContext();
  // Mobile (coarse pointer) gets a lite variant: fewer petals, slower fall.
  // Phones can't afford 24 composited animation layers — re-introduced after
  // an earlier blanket disable, hidden again automatically via body.fx-reduced
  // if the FPS monitor catches sustained <45fps.
  const [coarse] = useState(() => safeMatch('(pointer: coarse)'));
  const petals = useMemo(
    () => (coarse ? makePetals(10, 22, 10) : makePetals(24, 14, 10)),
    [coarse],
  );
  if (!fxEnabled) return null;
  return (
    <div
      className={`sakura-overlay${coarse ? ' sakura-overlay--lite' : ''}`}
      aria-hidden="true"
    >
      {petals.map((p, i) => (
        <span
          key={i}
          className={`sakura-petal${p.alt ? ' sakura-petal--alt' : ''}`}
          style={{
            left: `${p.left}%`,
            color: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        >
          <PetalSvg size={p.size} />
        </span>
      ))}
    </div>
  );
}

function PetalSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2 C 16 5, 18 11, 14 17 C 13 19, 12 21, 12 22 C 12 21, 11 19, 10 17 C 6 11, 8 5, 12 2 Z"
        fill="currentColor"
      />
    </svg>
  );
}
